import CONSTANTS from 'app/shared/utilities/constant';
import UtilityService from './utilities/utility.service';
import { isRadioStatChannelAFrequency } from './utils';
import { formatDate } from 'app/shared/utilities/time';
import { FieldSchemaUnit } from 'app/shared/collections/StatDefinition';

const UNKNOWN_VALUE = 'Unknown';

export const format = {
    latency: (v: number): string => {
        return v ? v.toFixed(2) + ' ms' : 'Timeout';
    },

    percentage: (v: number): string => {
        return (v ? (v * 100).toFixed(2) : '0') + '%';
    },
    connectivity: (v: number): string => {
        return format.percentage(v);
    },

    packets: (v: number): string => {
        return (v && v.toFixed(0)) || undefined;
    },

    timestamp: (v: string, timeZone: string): string => {
        return formatDate(new Date(v), timeZone);
    },

    active: (v: boolean): string => {
        return v ? 'Yes' : 'No';
    },

    activeRadio: (v: boolean): string => {
        return v ? 'Uplink' : 'Non-Uplink';
    },

    signalStrength: (v: number): string => {
        return _formatNumberMaxTwoDecimalPlaces(v, 'dBm');
    },

    noise: (v: number): string => {
        return format.signalStrength(v);
    },

    rssi: (v: number): string => {
        return format.signalStrength(v);
    },

    cost: (v: number): string => {
        if (v === null || v === undefined || isNaN(v)) {
            return UNKNOWN_VALUE;
        }
        return `${v}`; // the cost unit depends on the manufacturer, so leave it out and just return the value
    },

    snr: (v: number): string => {
        return _formatNumberMaxTwoDecimalPlaces(v, 'dB'); // dB not dBm
    },

    channel: (v: number): string => {
        if (v === null || v === undefined || isNaN(v) || v === 0) {
            return UNKNOWN_VALUE;
        }
        if (isRadioStatChannelAFrequency(v)) {
            return `${v} MHz`;
        }
        return `${v}`;
    },

    byterate: (v: number, units: FieldSchemaUnit[]): string => {
        if (v === null || v === undefined || isNaN(v)) {
            return UNKNOWN_VALUE;
        }
        return UtilityService.normalizeUnitToStr(units, v / 8, 'B/s');
    },

    megabitrate: (v: number, units: FieldSchemaUnit[]): string => {
        if (v === null || v === undefined || isNaN(v)) {
            return UNKNOWN_VALUE;
        }
        return UtilityService.normalizeUnitToStr(units, v, 'Mbit/s');
    },

    sinr: (v: number): string => {
        return _formatNumberMaxTwoDecimalPlaces(v, 'dB');
    },

    rsrq: (v: number): string => {
        return _formatNumberMaxTwoDecimalPlaces(v, 'dB');
    },

    rsrp: (v: number): string => {
        return _formatNumberMaxTwoDecimalPlaces(v, 'dBm');
    },

    channelUtilisation: (v: number): string => {
        if (v === null || v === undefined || isNaN(v)) {
            return 'Unknown';
        }
        return `${v}` + CONSTANTS.Measurement.CHANNEL_UTILISATION_UNIT;
    },

    throughputBitrate: (v: number, units: FieldSchemaUnit[]): string => {
        if (v === null || v === undefined || isNaN(v)) {
            return UNKNOWN_VALUE;
        }
        return UtilityService.normalizeUnitToStr(units, v, 'bit/s');
    },
};

function _formatNumberMaxTwoDecimalPlaces(v: number, unit: string): string {
    if (v === null || v === undefined || isNaN(v)) {
        return UNKNOWN_VALUE;
    }
    return `${+v.toFixed(2)} ${unit}`;
}
